import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FolderIcon } from "../images/icons/icons";
// import TopGymDropdown from "./TopGymDropdown";
// import { selectUser } from "./auth/auth.selector";

const PermanentSearchBar = () => {
  const navigate = useNavigate();
  // const userInfoData = useSelector(selectUser);
  const { userMappedFeaturesV1 } = useSelector((state) => state.features);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // const isClusterManager = userInfoData?.account_type === "cluster_manager";

  const getAllMenuItems = () => {
    if (!userMappedFeaturesV1?.data) return [];

    const itemsMap = new Map();

    userMappedFeaturesV1.data.forEach((menuGroup) => {
      if (menuGroup.path && !itemsMap.has(menuGroup.path)) {
        itemsMap.set(menuGroup.path, {
          label: menuGroup.label,
          path: menuGroup.path,
          type: "menu",
        });
      }

      if (menuGroup.sub_menu && menuGroup.sub_menu.length > 0) {
        menuGroup.sub_menu.forEach((subItem) => {
          if (subItem.path && !itemsMap.has(subItem.path)) {
            itemsMap.set(subItem.path, {
              label: subItem.label,
              path: subItem.path,
              type: "submenu",
              parent: menuGroup.label,
            });
          }
        });
      }
    });

    return Array.from(itemsMap.values());
  };

  const filterMenuItems = (term) => {
    const allItems = getAllMenuItems();
    if (!term.trim()) return [];

    return allItems.filter(
      (item) =>
        item.label.toLowerCase().includes(term.toLowerCase()) ||
        (item.parent && item.parent.toLowerCase().includes(term.toLowerCase()))
    );
  };

  const handleNavigate = (path) => {
    if (path) {
      navigate(path);
      setSearchTerm("");
      setSearchResults([]);
      setSelectedIndex(0);
      setIsResultsVisible(false);
      setIsDropdownOpen(false);
      searchInputRef.current?.blur();
    } else {
    }
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    if (!isResultsVisible && value.trim().length > 0) {
      setIsResultsVisible(true);
      setIsDropdownOpen(true);
    }
  };

  const handleResultClick = (path) => {
    handleNavigate(path);
  };

  const handleNewTabClick = (e, path) => {
    e.stopPropagation();
    e.preventDefault();
    if (path) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
    }
  };

  const handleKeyDown = (event) => {
    if (!isResultsVisible || searchResults.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedIndex((prev) =>
        prev < searchResults.length - 1 ? prev + 1 : 0
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : searchResults.length - 1
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (searchResults[selectedIndex]) {
        handleNavigate(searchResults[selectedIndex].path);
      }
    } else if (event.key === "Escape") {
      setIsResultsVisible(false);
      setIsDropdownOpen(false);
      searchInputRef.current?.blur();
    }
  };

  const handleFocus = () => {
    setIsResultsVisible(true);
    setIsDropdownOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsResultsVisible(false);
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let results;
    if (searchTerm.trim()) {
      results = filterMenuItems(searchTerm);
    } else {
      results = getAllMenuItems();
    }
    setSearchResults(results);
    setSelectedIndex(0);
  }, [searchTerm, userMappedFeaturesV1, isResultsVisible]);

  return (
    <div className="global-search-permanent-container" ref={containerRef}>
      {/* {isClusterManager && <TopGymDropdown />} */}

      <div
        className={`global-search-permanent-input-wrapper${
          isDropdownOpen ? " global-search-permanent-input-wrapper-open" : ""
        }`}
      >
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search dashboard pages..."
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          className="global-search-permanent-input"
        />
        <div className="global-search-permanent-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
          </svg>
        </div>
        {searchTerm && (
          <button
            className="global-search-permanent-clear"
            onClick={() => {
              setSearchTerm("");
              setSearchResults([]);
              setIsResultsVisible(false);
              setIsDropdownOpen(false);
              searchInputRef.current?.focus();
            }}
          >
            ×
          </button>
        )}
      </div>

      {isResultsVisible && (
        <div className="global-search-permanent-results">
          {searchResults.length > 0 ? (
            <>
              <div className="global-search-permanent-results-header">
                <span>
                  {searchResults.length} result
                  {searchResults.length !== 1 ? "s" : ""}
                </span>
              </div>
              {searchResults.map((result, index) => (
                <div
                  key={`${result.path}-${index}`}
                  className={`global-search-permanent-result-item ${
                    index === selectedIndex
                      ? "global-search-permanent-result-selected"
                      : ""
                  }`}
                  onMouseDown={() => handleResultClick(result.path)}
                  style={{ cursor: "pointer" }}
                >
                  <div>
                    <div className="global-search-permanent-result-label">
                      {result.label}
                    </div>
                    {result.parent && (
                      <div className="global-search-permanent-result-parent">
                        <span
                          className="global-search-folder-icon"
                          aria-label="folder"
                        >
                          <FolderIcon />
                        </span>
                        in <span className="uppercase">{result.parent}</span>
                      </div>
                    )}
                  </div>
                  <button
                    className="global-search-permanent-result-open-tab"
                    tabIndex={-1}
                    title="Open in new tab"
                    onMouseDown={(e) => handleNewTabClick(e, result.path)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14.5 10V14.5C14.5 15.3284 13.8284 16 13 16H5C4.17157 16 3.5 15.3284 3.5 14.5V7C3.5 6.17157 4.17157 5.5 5 5.5H9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M10.5 9.5L16.5 3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M13.5 3.5H16.5V6.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </>
          ) : (
            <div className="global-search-permanent-no-results">
              No pages found{searchTerm ? ` for "${searchTerm}"` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PermanentSearchBar;
